import type { Slide } from '@mentimeter/presentation-schema/api-types-overrides';
import { getIsQuizSlide } from '../../../utils/isQuizSlide';

/**
 * Determines the next visibility state (basically should it animate in or out) of the QR code sidebar for the slide based on slide type,
 * QR code visibility settings, quiz lobby state, and navigation steps.
 *
 * @param params - Configuration object for sidebar visibility determination
 * @param params.type - The type from staticContent
 * @param params.qrCodeVisibility - The visibility setting for the QR code on the slide ("Editor setting")
 * @param params.isInQuizLobby - Whether the user is currently in a quiz lobby (undefined if not applicable)
 * @param params.currentStep - The current navigation step
 * @param params.previousStep - The previous navigation step
 *
 * @returns `true` if the QR sidebar should be visible (start animating 'in'), `false` otherwise
 *
 * @remarks
 * Special handling includes:
 * - Quiz slides: Hides sidebar for leaderboard type and when not in quiz lobby
 * - Linear navigation: Hides sidebar when transitioning from 'hide-answers' to 'view-answers'
 * - Default behavior: Shows sidebar based on qrCodeVisibility setting
 */
export const getQrSidebarVisibility = ({
  type,
  qrCodeVisibility,
  isInQuizLobby,
  currentStep,
  previousStep,
}: {
  type: Slide['staticContent']['type'];
  qrCodeVisibility: Slide['qrCodeVisibility'];
  isInQuizLobby: boolean | undefined;
  currentStep: string | undefined;
  previousStep: string | undefined;
}) => {
  // Handle quiz slides separately
  if (getIsQuizSlide(type)) {
    // "quiz-choice" | "quiz-open" | "leaderboard"
    // If slide type is 'leaderboard': hide the sidebar
    if (type === 'leaderboard') {
      return false;
    }

    // if we are on a quiz slide but not in the lobby: hide the sidebar
    if (isInQuizLobby === false) {
      return false;
    }

    // if we are on a quiz slide and we are in the lobby: allow the sidebar to show based on qrCodeVisibility
    if (isInQuizLobby === true) {
      return qrCodeVisibility === 'visible';
    }
  }

  // While stepping from hide answers to view answers in the linear navigation flow we want to focus on the content and hide the QR code and sidebar "Zissou"
  if (previousStep === 'hide-answers' && currentStep === 'view-answers') {
    return false;
  }

  // Fallback to default behavior based on qrCodeVisibility
  return qrCodeVisibility === 'visible';
};
